/**
 * PostCard — displays a single iHerb post with approve/reject workflow
 */

import { useState } from 'react';
import { Card, Badge, Button } from '@lib/ui';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';
import type { IherbPost, PostStatus } from '../types';

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<PostStatus, string> = {
  queued: 'Queued',
  scraping: 'Scraping…',
  generating: 'Generating…',
  refining: 'Refining…',
  pending: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
  sent: 'Sent',
};

const STATUS_COLORS: Record<PostStatus, 'neutral' | 'info' | 'warning' | 'success' | 'error'> = {
  queued: 'neutral',
  scraping: 'info',
  generating: 'info',
  refining: 'info',
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  sent: 'neutral',
};

function StatusBadge({ status }: { status: PostStatus }) {
  return (
    <Badge color={STATUS_COLORS[status]} size="sm">
      {STATUS_LABELS[status]}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Spinner for in-progress states
// ---------------------------------------------------------------------------

function Spinner() {
  return (
    <svg
      className="size-4 animate-spin text-blue-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// PostCard
// ---------------------------------------------------------------------------

export interface PostCardProps {
  post: IherbPost;
  onApprove: (id: string) => void;
  onReject: (id: string, feedback: string) => void;
  onFeedback: (id: string, feedback: string) => void;
  onDelete: (id: string) => void;
  onSend: (id: string) => void;
  onRefine: (id: string) => Promise<void>;
  isUpdating: boolean;
  isSending: boolean;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
}

type ActionMode = 'none' | 'reject' | 'feedback' | 'confirmDelete';

export function PostCard({
  post,
  onApprove,
  onReject,
  onFeedback,
  onDelete,
  onSend,
  onRefine,
  isUpdating,
  isSending,
  isSelected,
  onToggleSelect,
}: PostCardProps) {
  const [mode, setMode] = useState<ActionMode>('none');
  const [feedbackText, setFeedbackText] = useState(post.feedback ?? '');
  const [isRefining, setIsRefining] = useState(false);

  const isInProgress = ['queued', 'scraping', 'generating', 'refining'].includes(post.status);
  const isReviewable =
    post.status === 'pending' || post.status === 'approved' || post.status === 'rejected';
  const isSendable = post.status === 'approved';

  const handleRejectSubmit = () => {
    onReject(post.id, feedbackText);
    setMode('none');
  };

  const handleFeedbackSubmit = () => {
    onFeedback(post.id, feedbackText);
    setMode('none');
  };

  return (
    <Card
      skin="bordered"
      className={`overflow-hidden transition-shadow ${isSelected ? 'ring-2 ring-primary-500' : ''}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-200 dark:border-dark-600">
        <div className="flex items-center gap-2 min-w-0">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(post.id)}
            className="size-4 shrink-0 cursor-pointer accent-primary-600"
            aria-label="Select post"
          />
          {isInProgress && <Spinner />}
          <StatusBadge status={post.status} />
          {post.price && (
            <span className="text-sm font-medium text-gray-700 dark:text-dark-200">
              {post.price}
            </span>
          )}
          {post.click_count > 0 && (
            <span
              className="text-xs font-medium text-gray-600 dark:text-dark-300"
              title={`${post.click_count} click${post.click_count === 1 ? '' : 's'}`}
            >
              🔗 {post.click_count}
            </span>
          )}
        </div>
        <button
          onClick={() => setMode('confirmDelete')}
          className="shrink-0 text-gray-400 hover:text-red-500 dark:text-dark-400 dark:hover:text-red-400 transition-colors"
          aria-label="Delete post"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="size-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Product image */}
      {post.image_url && (
        <div className="bg-gray-50 dark:bg-dark-800 flex justify-center p-3 border-b border-gray-200 dark:border-dark-600">
          <img
            src={`${API_BASE}${post.image_url}`}
            alt="Product"
            className="h-40 w-40 object-contain"
            loading="lazy"
          />
        </div>
      )}

      {/* Body */}
      <div className="p-4 space-y-3">
        {post.message ? (
          <p
            dir="rtl"
            lang="he"
            className="text-sm text-gray-800 dark:text-dark-100 whitespace-pre-wrap leading-relaxed"
          >
            {post.message}
          </p>
        ) : isInProgress ? (
          <p className="text-sm text-gray-400 dark:text-dark-400 italic">
            {STATUS_LABELS[post.status]}
          </p>
        ) : null}

        {post.product_url && (
          <a
            href={post.product_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block truncate text-xs text-primary-600 hover:underline dark:text-primary-400"
          >
            {post.product_url}
          </a>
        )}

        {post.short_url && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-dark-400">
            <span className="font-medium uppercase tracking-wide opacity-70">Short:</span>
            <a
              href={post.short_url}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate text-primary-600 hover:underline dark:text-primary-400"
              title={post.short_url}
            >
              {post.short_url.replace(/^https?:\/\//, '')}
            </a>
          </div>
        )}

        {post.feedback && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide opacity-70">
              Feedback
            </p>
            {post.feedback}
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      {mode === 'confirmDelete' && (
        <div className="px-4 pb-4 space-y-2">
          <p className="text-sm text-gray-700 dark:text-dark-200">Delete this post?</p>
          <div className="flex gap-2">
            <Button color="error" size="sm" onClick={() => onDelete(post.id)}>
              Delete
            </Button>
            <Button variant="flat" size="sm" onClick={() => setMode('none')}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Approve / Reject actions — shown for pending, approved, and rejected */}
      {isReviewable && mode === 'none' && (
        <div className="flex flex-wrap gap-2 px-4 pb-4">
          {post.status !== 'approved' && (
            <Button
              color="success"
              size="sm"
              onClick={() => onApprove(post.id)}
              disabled={isUpdating}
            >
              Approve
            </Button>
          )}
          {isSendable && (
            <Button color="primary" size="sm" onClick={() => onSend(post.id)} disabled={isSending}>
              {isSending ? 'Sending…' : 'Send'}
            </Button>
          )}
          {post.status !== 'rejected' && (
            <Button
              color="error"
              variant="soft"
              size="sm"
              onClick={() => {
                setMode('reject');
                setFeedbackText('');
              }}
              disabled={isUpdating}
            >
              Reject
            </Button>
          )}
          <Button
            variant="flat"
            size="sm"
            onClick={() => {
              setMode('feedback');
              setFeedbackText(post.feedback ?? '');
            }}
          >
            {post.feedback ? 'Edit Feedback' : 'Add Feedback'}
          </Button>
          {post.message && (
            <Button
              variant="flat"
              size="sm"
              onClick={async () => {
                setIsRefining(true);
                try {
                  await onRefine(post.id);
                } finally {
                  setIsRefining(false);
                }
              }}
              disabled={isRefining}
            >
              {isRefining ? 'Refining…' : 'Refine'}
            </Button>
          )}
        </div>
      )}

      {/* Reject form */}
      {mode === 'reject' && (
        <div className="px-4 pb-4 space-y-2">
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Rejection reason (optional)"
            rows={2}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-dark-500 dark:bg-dark-800 dark:text-white resize-none"
          />
          <div className="flex gap-2">
            <Button color="error" size="sm" onClick={handleRejectSubmit} disabled={isUpdating}>
              Confirm Reject
            </Button>
            <Button variant="flat" size="sm" onClick={() => setMode('none')}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Feedback form */}
      {mode === 'feedback' && (
        <div className="px-4 pb-4 space-y-2">
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Your feedback"
            rows={2}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-dark-500 dark:bg-dark-800 dark:text-white resize-none"
          />
          <div className="flex gap-2">
            <Button color="primary" size="sm" onClick={handleFeedbackSubmit} disabled={isUpdating}>
              Save
            </Button>
            <Button variant="flat" size="sm" onClick={() => setMode('none')}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
